import { Volume2, VolumeOff } from 'lucide-react';


let videoElement
let adsLoaded = false
let adContainer
let adDisplayContainer
let adsLoader
let adsManager
let adProgressBar
let muteToggle
let isMuted = false // state tracker

// Progress bar helpers
let progressTimer = null
let currentAdDuration = null // seconds

const unmuteIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume-x-icon lucide-volume-x"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><line x1="22" x2="16" y1="9" y2="15"/><line x1="16" x2="22" y1="9" y2="15"/></svg>`

const muteIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume2-icon lucide-volume-2"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.364 18.364a9 9 0 0 0 0-12.728"/></svg>`



// ================================
// Initialize IMA SDK
// ================================
export function initializeIMA(videoEl, vastURL) {
  videoElement = videoEl
  adContainer = document.getElementsByClassName('ad-container')[0]
  adProgressBar = document.getElementsByClassName('ad-progress-bar')[0]
  muteToggle = document.getElementsByClassName('ad-mute-toggle')[0]

  // Basic checks
  if (!adContainer) {
    console.error('initializeIMA: .ad-container not found')
    return
  }
  if (!adProgressBar) {
    console.warn('initializeIMA: .ad-progress-bar not found — progress UI will be disabled')
  }
  if (!muteToggle) {
    console.warn('initializeIMA: .ad-mute-toggle not found — mute control disabled')
  }

  // Make sure ad container exists for IMA (don't put controls inside it)
  adDisplayContainer = new window.google.ima.AdDisplayContainer(adContainer, videoElement)
  adsLoader = new window.google.ima.AdsLoader(adDisplayContainer)

  // Attach listeners to the loader
  adsLoader.addEventListener(
    window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
    onAdsManagerLoaded,
    false
  )
  adsLoader.addEventListener(
    window.google.ima.AdErrorEvent.Type.AD_ERROR,
    onAdError,
    false
  )

  // Let the SDK know content ended
  videoElement.addEventListener('ended', () => {
    try {
      adsLoader.contentComplete()
    } catch (e) {}
  })

  // Request ads
  const adsRequest = new window.google.ima.AdsRequest()
  adsRequest.adTagUrl = vastURL

  adsRequest.linearAdSlotWidth = videoElement.clientWidth
  adsRequest.linearAdSlotHeight = videoElement.clientHeight
  adsRequest.nonLinearAdSlotWidth = videoElement.clientWidth
  adsRequest.nonLinearAdSlotHeight = Math.floor(videoElement.clientHeight / 3)

  adsLoader.requestAds(adsRequest)

  // Handle visibility changes (resume ad/content when user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      try {
        if (
          adsManager &&
          typeof adsManager.getRemainingTime === 'function' &&
          adsManager.getRemainingTime() > 0
        ) {
          adsManager.resume()
        } else {
          videoElement.play().catch(() => {})
        }
      } catch (e) {
        videoElement.play().catch(() => {})
      }
    }
  })

  // Keep ad slot sized correctly
  window.addEventListener('resize', () => {
    if (!adsManager) return
    try {
      const w = videoElement.clientWidth
      const h = videoElement.clientHeight
      adsManager.resize(w, h, window.google.ima.ViewMode.NORMAL)
    } catch (e) {}
  })

  // ================================
  // Mute/Unmute toggle listener
  // ================================
  if (muteToggle) {
    muteToggle.addEventListener('click', () => {
      if (!adsManager) return

      isMuted = !isMuted
      try {
        adsManager.setVolume(isMuted ? 0 : 1)
      } catch (e) {
        console.warn('Failed to toggle mute state', e)
      }

      muteToggle.innerHTML = isMuted ? unmuteIcon : muteIcon
    })
  }
}

// ================================
// Load ads (must be user gesture on mobile)
// ================================
export function loadAds() {
  if (adsLoaded) return
  adsLoaded = true

  try {
    adDisplayContainer.initialize()
  } catch (e) {}

  const width = videoElement.clientWidth
  const height = videoElement.clientHeight
  try {
    adsManager.init(width, height, window.google.ima.ViewMode.NORMAL)
    adsManager.start()
  } catch (adError) {
    console.error('AdsManager could not be started', adError)
    videoElement.play()
  }
}

// ================================
// AdsManager loaded
// ================================
function onAdsManagerLoaded(evt) {
  adsManager = evt.getAdsManager(videoElement)

  adsManager.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError)
  adsManager.addEventListener(
    window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
    onContentPauseRequested
  )
  adsManager.addEventListener(
    window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
    onContentResumeRequested
  )
  adsManager.addEventListener(window.google.ima.AdEvent.Type.LOADED, onAdLoaded)
  adsManager.addEventListener(window.google.ima.AdEvent.Type.STARTED, onAdStarted)
  adsManager.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, onAdComplete)
  adsManager.addEventListener(window.google.ima.AdEvent.Type.SKIPPED, onAdSkipped)
  adsManager.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, onAllAdsCompleted)
}

// ================================
// Error handling
// ================================
function onAdError(adErrorEvent) {
  try {
    console.error(
      'IMA error:',
      adErrorEvent && adErrorEvent.getError ? adErrorEvent.getError() : adErrorEvent
    )
  } catch (e) {
    console.error('IMA error (caught):', e)
  }

  if (adsManager) {
    try {
      adsManager.destroy()
    } catch (e) {}
    adsManager = null
  }

  if (adContainer) adContainer.style.display = 'none'
  hideProgressBar()
  hideMuteToggle()
  stopProgressInterval()

  videoElement.play().catch(() => {})
  adsLoaded = false
  currentAdDuration = null
}

// ================================
// Content <-> Ad transition handlers
// ================================
function onContentPauseRequested() {
  videoElement.pause()
  if (adContainer) adContainer.style.display = 'block'
  showProgressBar()
  showMuteToggle()
}

function onContentResumeRequested() {
  if (adContainer) adContainer.style.display = 'none'
  hideProgressBar()
  hideMuteToggle()
  stopProgressInterval()
  videoElement.play().catch(() => {})
  currentAdDuration = null
}

// ================================
// Individual ad events
// ================================
function onAdLoaded(adEvent) {
  try {
    const ad = adEvent && typeof adEvent.getAd === 'function' ? adEvent.getAd() : null
    if (!ad || !ad.isLinear()) {
      videoElement.play().catch(() => {})
    }
  } catch (e) {}
}

function onAdStarted(adEvent) {
  try {
    const ad = adEvent && typeof adEvent.getAd === 'function' ? adEvent.getAd() : null
    if (ad && typeof ad.getDuration === 'function') {
      currentAdDuration = ad.getDuration()
    } else if (adsManager && typeof adsManager.getCurrentAd === 'function') {
      const cur = adsManager.getCurrentAd()
      if (cur && typeof cur.getDuration === 'function') {
        currentAdDuration = cur.getDuration()
      } else {
        currentAdDuration = null
      }
    }
  } catch (e) {
    currentAdDuration = null
  }

  if (adContainer) adContainer.style.display = 'block'
  showProgressBar()
  showMuteToggle()
  startProgressInterval()
}

function onAdComplete() {
  hideProgressBar()
  hideMuteToggle()
  stopProgressInterval()
  currentAdDuration = null
}

function onAllAdsCompleted() {
  if (adContainer) adContainer.style.display = 'none'
  hideProgressBar()
  hideMuteToggle()
  stopProgressInterval()

  if (adsManager) {
    try {
      adsManager.destroy()
    } catch (e) {}
    adsManager = null
  }
  adsLoaded = false
  currentAdDuration = null
}

function onAdSkipped() {
  hideProgressBar()
  hideMuteToggle()
  stopProgressInterval()
  currentAdDuration = null
}

// ================================
// Progress bar logic
// ================================
function startProgressInterval() {
  stopProgressInterval()
  if (!adProgressBar) return

  progressTimer = setInterval(() => {
    try {
      let remaining = null
      if (adsManager && typeof adsManager.getRemainingTime === 'function') {
        remaining = adsManager.getRemainingTime()
      }

      if ((currentAdDuration || currentAdDuration === 0) && (remaining !== null && remaining !== undefined)) {
        const dur = Number(currentAdDuration)
        if (dur > 0) {
          const frac = Math.max(0, Math.min(1, (dur - remaining) / dur))
          setProgressWidth(frac)
          return
        }
      }

      if (adsManager && typeof adsManager.getDuration === 'function') {
        const maybeDur = adsManager.getDuration()
        if (maybeDur && remaining !== null && remaining !== undefined) {
          const frac = Math.max(0, Math.min(1, (maybeDur - remaining) / maybeDur))
          setProgressWidth(frac)
          return
        }
      }
    } catch (e) {}
  }, 200)
}

function stopProgressInterval() {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

function showProgressBar() {
  if (!adProgressBar) return
  adProgressBar.style.display = 'block'
  adProgressBar.style.width = '0%'
}

function hideProgressBar() {
  if (!adProgressBar) return
  adProgressBar.style.display = 'none'
  adProgressBar.style.width = '0%'
}

function showMuteToggle() {
  if (!muteToggle) return
  muteToggle.style.display = 'block'
  muteToggle.innerHTML = isMuted ? unmuteIcon : muteIcon
}

function hideMuteToggle() {
  if (!muteToggle) return
  muteToggle.style.display = 'none'
}

function setProgressWidth(fraction) {
  if (!adProgressBar) return
  const pct = Math.max(0, Math.min(100, fraction * 100))
  adProgressBar.style.width = pct + '%'
}
